// ChipTextEditor — edit prose whose links are chips. The text is editable as
// plain text; each markdown link is one atomic, non-editable chip (deleted as
// a whole, never half-edited). Every change is read back from the DOM into
// tokens and reported as markdown (`onChange`), so the caller keeps holding
// markdown and the author never sees link syntax.
//
// The impure leaf of the correction view: the DOM is the editing surface, so
// it is seeded once from `initial` (React never re-renders the content, which
// would reset the caret) and read on every input.

import { type ReactNode, useState } from "react";
import { LinkChip } from "../../components/Chat/InlineLink/InlineLink.js";
import type { IconName } from "../../components/Icon/Icon.js";
import { type Token, toMarkdown, toTokens } from "./linkTokens.js";

const NEWLINE: Token = { type: "text", text: "\n" };

// The DOM content back to tokens: text nodes are text, a chip is its link,
// and the line breaks a browser inserts (<br>, or a block per line) are "\n".
const readTokens = (node: Node): readonly Token[] =>
    [...node.childNodes].flatMap((child, index): readonly Token[] => {
        if (child.nodeType === Node.TEXT_NODE)
            return [{ type: "text", text: child.nodeValue ?? "" }];
        if (!(child instanceof HTMLElement)) return [];
        const href = child.dataset.href;
        if (href !== undefined)
            return [{ type: "link", href, label: child.dataset.label ?? "" }];
        if (child.tagName === "BR") return [NEWLINE];
        const inner = readTokens(child);
        return child.tagName === "DIV" || child.tagName === "P"
            ? [...(index > 0 ? [NEWLINE] : []), ...inner]
            : inner;
    });

export const ChipTextEditor = ({
    initial,
    glyphOf,
    onChange,
    ariaLabel,
}: {
    readonly initial: string;
    readonly glyphOf: (href: string) => IconName;
    readonly onChange: (markdown: string) => void;
    readonly ariaLabel: string;
}): ReactNode => {
    // Seeded once; the DOM owns the content from here on.
    const [tokens] = useState(() => toTokens(initial));
    return (
        // biome-ignore lint/a11y/useSemanticElements: a <textarea> cannot hold chips; a contentEditable textbox is the standard rich-text form.
        <div
            className="jd-editor"
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label={ariaLabel}
            tabIndex={0}
            onInput={(event) =>
                onChange(toMarkdown(readTokens(event.currentTarget)))
            }
        >
            {tokens.map((token, i) =>
                token.type === "text" ? (
                    token.text
                ) : (
                    <span
                        // biome-ignore lint/suspicious/noArrayIndexKey: the seeded token list never reorders.
                        key={i}
                        className="jd-editorChip"
                        contentEditable={false}
                        data-href={token.href}
                        data-label={token.label}
                    >
                        <LinkChip glyph={glyphOf(token.href)}>
                            {token.label}
                        </LinkChip>
                    </span>
                ),
            )}
        </div>
    );
};
